import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Zap, Wrench, Clock3, Activity, Info, DatabaseZap,
    ClipboardList, Loader2, Download, ShieldCheck, Terminal, MessageCircle,
} from "lucide-react";
import { api } from "../../lib/api";
import RunCustomScriptModal from "./RunCustomScriptModal";
import SystemLogsSection from "./SystemLogsSection";
import { usePageMeta } from "../../components/Layout";

/**
 * Phase D0 + D1 + Human-authorised backup addition — Admin Tools landing.
 *
 * D0: read-only status cards (env, reminder worker, cadence, meta events).
 * D1: legacy enquiry migration — non-mutating preview + gated execute.
 * Human-authorised: on-demand database backup download; the migration
 * Execute flow now runs backup → download → then migration execute using
 * a shared operation_id, and the backend refuses execute if no linked
 * successful backup audit row exists.
 */
export default function AdminToolsPage() {
    usePageMeta({ title: "Tools | Studio G4 Admin" });
    const [status, setStatus] = useState(null);
    const [err, setErr] = useState("");
    const [showRunScript, setShowRunScript] = useState(false);
    useEffect(() => {
        let alive = true;
        api.get("/admin/tools/status")
            .then(r => { if (alive) setStatus(r.data); })
            .catch(e => { if (alive) setErr(e?.response?.data?.detail || e.message || "Failed"); });
        return () => { alive = false; };
    }, []);

    const env = status?.environment?.app_env || "unknown";
    const dbName = status?.environment?.db_name || "—";

    return (
        <div className="flex flex-col gap-6" data-testid="admin-tools-page">
            <header>
                <h1 className="font-manrope text-3xl font-semibold text-charcoal mb-1 flex items-center gap-2">
                    <Wrench size={22} /> Tools
                </h1>
                <p className="text-[#666] text-[15px]">
                    Operational admin utilities. Consequential
                    production operations are gated behind an
                    explicit environment confirmation. Environment
                    identity is surfaced by the Environment card
                    below (backend-authoritative).
                </p>
            </header>

            {err && (
                <div data-testid="tools-load-error"
                     className="text-[12px] text-terracotta">
                    {err}
                </div>
            )}

            <section className="flex flex-col gap-3" data-testid="tools-group-communication">
                <h2 className="font-manrope font-semibold text-[16px] text-charcoal">Communication</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <ContactExportSection />
                    <WhatsAppBuilderSection />
                </div>
            </section>

            <section className="flex flex-col gap-3" data-testid="tools-group-dev-tools">
                <h2 className="font-manrope font-semibold text-[16px] text-charcoal">Dev Tools</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* §Run-Script Pass — Ad-hoc admin script runner. */}
                    <button
                        onClick={() => setShowRunScript(true)}
                        data-testid="tools-run-script-card"
                        className="text-left rounded-lg border border-[#E5E2DC] bg-white p-4 hover:border-terracotta transition-colors">
                        <div className="flex items-center gap-2">
                            <Terminal size={16} className="text-terracotta" />
                            <div className="font-manrope font-semibold text-[14px]">Run Custom Script</div>
                        </div>
                        <p className="text-[12px] text-[#666] mt-1 leading-snug">
                            Execute a custom Python or shell script directly on the server. Use for ad-hoc database operations or maintenance tasks.
                        </p>
                        <span
                            data-testid="tools-run-script-open"
                            className="mt-3 inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md bg-charcoal text-[#F5F3EE]">
                            Open Editor
                        </span>
                    </button>

                    <MigrationCard env={env} />

                    <BackupCard
                        env={env}
                        dbName={dbName}
                        engine="mongodump"
                        testIdSuffix="mongodump"
                        title="Database backup — mongodump"
                        subtitle={"Uses the mongodb-database-tools mongodump binary. Produces a native mongorestore --archive-compatible gzip blob. Requires the binary on PATH in the container."}
                        iconTone="text-terracotta"
                    />

                    <BackupCard
                        env={env}
                        dbName={dbName}
                        engine="python"
                        testIdSuffix="python"
                        title="Database backup — Python fallback"
                        subtitle={"Pure-Python engine iterating collections via pymongo. Produces a gzipped tar of per-collection .bson + .metadata.json files, natively readable by mongorestore --gzip --dir. Works regardless of container tooling."}
                        iconTone="text-charcoal"
                    />
                </div>
            </section>

            <section className="flex flex-col gap-3" data-testid="tools-group-system-logs">
                <h2 className="font-manrope font-semibold text-[16px] text-charcoal">System Logs</h2>
                <SystemLogsSection />
            </section>

            <section className="flex flex-col gap-3" data-testid="tools-group-platform-info">
                <h2 className="font-manrope font-semibold text-[16px] text-charcoal">Platform info</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <StatusCard testid="tools-card-environment"
                        icon={Info}
                        title="Environment"
                        kv={[
                            ["APP_ENV", status?.environment?.app_env || "—"],
                            ["Database", dbName],
                        ]}
                        footer="Displayed so any operational action is unambiguously scoped to the intended environment." />

                    <ToolCard
                        to="/admin/meta-events"
                        testid="tools-card-meta-events"
                        icon={Zap}
                        title="Meta events"
                        description="Inspect Meta CAPI event lifecycle: recent deliveries, failed permanent events, retry evidence. Existing operational route relocated here from the main sidebar." />

                    <StatusCard testid="tools-card-reminder-worker"
                        icon={Activity}
                        title="Reminder worker"
                        kv={[
                            ["Status", status?.reminder_worker?.running
                                ? "running" : "stopped"],
                        ]}
                        footer="Read-only inspection. The worker is started at application boot; restart controls are not exposed here." />

                    <StatusCard testid="tools-card-cadence-config"
                        icon={Clock3}
                        title="Reminder cadence configuration"
                        kv={[
                            ["Effective config",
                                status?.cadence?.effective_config
                                    ? `key=${status.cadence.effective_config.key} · content=${status.cadence.effective_config.content_status || "-"}`
                                    : "seed defaults only"],
                            ["Fixture defaults",
                                status?.cadence?.fixture_defaults
                                    ? Object.keys(status.cadence.fixture_defaults).join(", ")
                                    : "—"],
                        ]}
                        footer="Read-only. Production cadence values are locked externally by Strategy and applied via cadence-config write; no editor is exposed here." />
                </div>
            </section>

            {showRunScript && (
                <RunCustomScriptModal onClose={() => setShowRunScript(false)} />
            )}
        </div>
    );
}


function ContactExportSection() {
    const [filter, setFilter] = useState("active_projects");
    const [count, setCount] = useState(null);
    const [skipped, setSkipped] = useState(null);
    const [busy, setBusy] = useState(false);
    const [lastExport, setLastExport] = useState(null);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const loadCount = useCallback(async (f) => {
        try {
            const r = await api.get(`/admin/tools/contact-export/count?filter=${f}`);
            setCount(r.data?.count ?? 0);
            setSkipped(r.data?.skipped || null);
        } catch (e) {
            setCount(null);
            setErr(e?.response?.data?.detail || "Failed to load count");
        }
    }, []);
    const loadHistory = useCallback(async () => {
        try {
            const r = await api.get("/admin/tools/contact-export/history?limit=1");
            const item = (r.data?.items || [])[0];
            setLastExport(item || null);
        } catch { setLastExport(null); }
    }, []);
    useEffect(() => { loadCount(filter); }, [filter, loadCount]);
    useEffect(() => { loadHistory(); }, [loadHistory]);

    const doExport = async () => {
        setBusy(true); setErr(""); setMsg("");
        try {
            const opId = `export-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const r = await api.post(
                "/admin/tools/contact-export",
                { filter, create_op_id: opId },
                { responseType: "blob" },
            );
            const cd = r.headers["content-disposition"] || "";
            const m = /filename="([^"]+)"/.exec(cd);
            const fname = m ? m[1] : `sg4-contacts-${filter}.csv`;
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url; a.download = fname;
            document.body.appendChild(a); a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            setMsg(`Downloaded ${fname}.`);
            loadHistory();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Export failed");
        } finally { setBusy(false); }
    };

    const skipSummary = skipped ? Object.entries(skipped)
        .filter(([_, v]) => v > 0)
        .map(([k, v]) => `${k.replace(/_/g, " ")} (${v})`)
        .join(", ") : "";

    return (
        <section className="rounded-lg border border-[#E5E2DC] bg-white p-5"
            data-testid="contact-export-section">
            <div className="flex items-center gap-2 mb-2">
                <Download size={16} className="text-charcoal" />
                <h2 className="font-manrope font-semibold text-[15px] text-charcoal">Contact Export</h2>
            </div>
            <p className="text-[13px] text-[#666] mb-3">
                Download client contacts as a Google Contacts-compatible CSV.
            </p>
            <div className="flex flex-col gap-1.5 mb-3">
                {[
                    { v: "all_clients", label: "All clients" },
                    { v: "active_projects", label: "Active projects" },
                    { v: "new_since_last_export", label: "New since last export" },
                ].map(o => (
                    <label key={o.v} className="text-[13px] flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="contact-export-filter"
                            value={o.v} checked={filter === o.v}
                            onChange={() => setFilter(o.v)}
                            data-testid={`contact-export-filter-${o.v}`} />
                        {o.label}
                    </label>
                ))}
            </div>
            <div className="text-[12px] text-[#666] mb-2" data-testid="contact-export-count">
                {count === null ? "…" :
                    `${count} contact${count === 1 ? "" : "s"} will be exported`}
                {skipSummary ? ` · skipped: ${skipSummary}` : ""}
            </div>
            <button type="button" onClick={doExport} disabled={busy || count === 0}
                data-testid="contact-export-run"
                className="text-[13px] px-3 py-1.5 rounded bg-charcoal text-white disabled:opacity-50">
                {busy ? "Exporting…" : "Export Contacts"}
            </button>
            {msg && <div className="text-[11.5px] text-[#345E34] mt-2"
                data-testid="contact-export-msg">{msg}</div>}
            {err && <div className="text-[11.5px] text-terracotta mt-2"
                data-testid="contact-export-err">{err}</div>}
            <div className="text-[11.5px] text-[#888] mt-3"
                data-testid="contact-export-last">
                {lastExport
                    ? `Last export: ${new Date(lastExport.exported_at).toLocaleString()} (${lastExport.filter}, ${lastExport.contact_count} contacts)`
                    : "No previous exports."}
            </div>
        </section>
    );
}


function ToolCard({ to, testid, icon: Icon, title, description }) {
    return (
        <Link to={to} data-testid={testid}
              className="rounded-lg border border-[#E5E2DC] bg-white p-4 hover:border-terracotta transition-colors">
            <div className="flex items-center gap-2">
                <Icon size={16} className="text-terracotta" />
                <div className="font-manrope font-semibold text-[14px]">{title}</div>
            </div>
            <p className="text-[12px] text-[#666] mt-1 leading-snug">{description}</p>
        </Link>
    );
}


function StatusCard({ testid, icon: Icon, title, kv, footer }) {
    return (
        <div data-testid={testid} className="rounded-lg border border-[#E5E2DC] bg-white p-4">
            <div className="flex items-center gap-2">
                <Icon size={16} className="text-terracotta" />
                <div className="font-manrope font-semibold text-[14px]">{title}</div>
            </div>
            <dl className="mt-3 space-y-1">
                {kv.map(([k, v]) => (
                    <div key={k} className="flex justify-between text-[12px]">
                        <dt className="text-[#666]">{k}</dt>
                        <dd className="font-mono text-charcoal text-right max-w-[65%] break-all">{v}</dd>
                    </div>
                ))}
            </dl>
            {footer && (
                <p className="text-[11px] text-[#888] mt-3 leading-snug">{footer}</p>
            )}
        </div>
    );
}


// ---------- Backup helpers ----------

// Shared backup-download primitive used by BackupCard AND MigrationCard.
// Returns {ok, auditId, filename, size, error} — never throws for HTTP
// errors; the caller decides how to render.
async function downloadBackup({ migrationOperationId, engine = "mongodump" } = {}) {
    try {
        const params = new URLSearchParams();
        params.set("engine", engine);
        if (migrationOperationId) params.set("migration_operation_id", migrationOperationId);
        // Use axios with blob to stream the archive to memory and trigger
        // a browser-native download once complete.
        const resp = await api.post(`/admin/tools/backup?${params.toString()}`, null, {
            responseType: "blob",
        });
        const auditId = resp.headers["x-lrs-backup-audit-id"] || null;
        const filename = resp.headers["x-lrs-backup-filename"]
            || `stage5-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.gz`;
        const engineHdr = resp.headers["x-lrs-backup-engine"] || engine;
        const blob = resp.data;
        // Trigger download
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        return { ok: true, auditId, filename, size: blob.size, engine: engineHdr };
    } catch (e) {
        let detail = e?.response?.data;
        if (detail instanceof Blob) {
            try {
                const txt = await detail.text();
                try { detail = JSON.parse(txt).detail; }
                catch { detail = txt; }
            } catch { detail = "backup failed"; }
        }
        return { ok: false, error: detail || e.message || "backup failed" };
    }
}


// ---------- Human-authorised: DUAL Database backup cards ----------


function BackupCard({ env, dbName, engine, title, subtitle, testIdSuffix, iconTone }) {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");

    async function runBackup() {
        setBusy(true);
        setError("");
        setResult(null);
        const r = await downloadBackup({ engine });
        if (r.ok) {
            setResult(r);
        } else {
            setError(String(r.error));
        }
        setBusy(false);
    }

    return (
        <div data-testid={`tools-card-backup-${testIdSuffix}`}
             className="rounded-lg border border-[#E5E2DC] bg-white p-4">
            <div className="flex items-center gap-2">
                <ShieldCheck size={16} className={iconTone} />
                <div className="font-manrope font-semibold text-[14px]">
                    {title}
                </div>
            </div>
            <p className="text-[12px] text-[#666] mt-1 leading-snug">
                {subtitle}{" "}
                Database <code data-testid={`backup-card-db-name-${testIdSuffix}`}>{dbName}</code>{" "}
                · environment <code data-testid={`backup-card-env-name-${testIdSuffix}`}>{env}</code>.
                Streamed directly to your browser; nothing is persisted on the server.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                    data-testid={`backup-run-btn-${testIdSuffix}`}
                    onClick={runBackup}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-charcoal text-offwhite text-[12px] font-manrope disabled:opacity-60">
                    {busy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    {busy ? "Downloading…" : "Download database backup"}
                </button>
                {result && (
                    <span data-testid={`backup-success-summary-${testIdSuffix}`}
                          className="text-[11px] text-[#333] font-mono">
                        ✓ {result.engine} · audit {String(result.auditId).slice(0, 8)}…
                        · {formatBytes(result.size)}
                    </span>
                )}
            </div>

            {error && (
                <div data-testid={`backup-error-${testIdSuffix}`}
                     className="text-[12px] text-terracotta mt-2">
                    {error}
                </div>
            )}
        </div>
    );
}


function formatBytes(bytes) {
    if (bytes == null) return "—";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
}


// ---------- D1 + backup-first: Legacy enquiry migration card ----------


function MigrationCard({ env }) {
    const [preview, setPreview] = useState(null);
    const [previewErr, setPreviewErr] = useState("");
    const [previewing, setPreviewing] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [ack, setAck] = useState(false);
    const [backupEngine, setBackupEngine] = useState("");  // no default — operator must pick
    const [phase, setPhase] = useState("idle"); // idle | backup | migrate | done
    const [executeResult, setExecuteResult] = useState(null);
    const [executeErr, setExecuteErr] = useState("");
    const [backupInfo, setBackupInfo] = useState(null);

    async function runPreview() {
        setPreviewing(true);
        setPreviewErr("");
        try {
            const r = await api.post("/admin/tools/migration/preview");
            setPreview(r.data);
        } catch (e) {
            setPreviewErr(e?.response?.data?.detail || e.message || "Preview failed");
        } finally {
            setPreviewing(false);
        }
    }

    const runExecute = useCallback(async () => {
        setExecuteErr("");
        setExecuteResult(null);
        setBackupInfo(null);

        // Generate a shared operation_id linking backup ↔ migration.
        const operationId =
            (window.crypto && window.crypto.randomUUID)
                ? window.crypto.randomUUID()
                : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

        // 1. Take backup FIRST — with the operator's chosen engine.
        setPhase("backup");
        const b = await downloadBackup({
            migrationOperationId: operationId,
            engine: backupEngine,
        });
        if (!b.ok) {
            setExecuteErr(`Pre-migration backup failed: ${b.error}`);
            setPhase("idle");
            return;
        }
        setBackupInfo(b);

        // 2. Backup complete → run migration referencing the same op id + engine.
        setPhase("migrate");
        try {
            const r = await api.post("/admin/tools/migration/execute", {
                confirm_env: confirmText,
                acknowledge: ack,
                operation_id: operationId,
                backup_engine: backupEngine,
            });
            setExecuteResult(r.data);
            setConfirmOpen(false);
            setPhase("done");
            runPreview();
        } catch (e) {
            setExecuteErr(e?.response?.data?.detail || e.message || "Execute failed");
            setPhase("idle");
        }
    }, [confirmText, ack, backupEngine]);

    const executing = phase === "backup" || phase === "migrate";
    const canExecute =
        confirmText.trim().toLowerCase() === env.trim().toLowerCase()
        && ack
        && (backupEngine === "mongodump" || backupEngine === "python");

    return (
        <div data-testid="tools-card-migration"
             className="rounded-lg border border-[#E5E2DC] bg-white p-4">
            <div className="flex items-center gap-2">
                <DatabaseZap size={16} className="text-terracotta" />
                <div className="font-manrope font-semibold text-[14px]">
                    Legacy enquiry migration
                </div>
            </div>
            <p className="text-[12px] text-[#666] mt-1 leading-snug max-w-[820px]">
                Maps existing raw enquiries into valid Stage 5 WS1
                projects. Additive — the same enquiry document gains
                Stage 5 fields in place. Non-Stage-5 legacy business
                values are preserved as <code>legacy_*</code>{" "}
                provenance. No downstream (WS2–WS7) records, comms,
                RFIs, or client actions are fabricated.{" "}
                <strong>Execute now downloads a pre-migration backup
                first;</strong> the migration only runs if that
                backup completed successfully.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                    data-testid="migration-run-preview-btn"
                    onClick={runPreview}
                    disabled={previewing}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-charcoal text-offwhite text-[12px] font-manrope disabled:opacity-60">
                    {previewing ? <Loader2 size={12} className="animate-spin" /> : <ClipboardList size={12} />}
                    Run preview (non-mutating)
                </button>
                <button
                    data-testid="migration-open-execute-btn"
                    onClick={() => { setConfirmText(""); setAck(false); setBackupEngine(""); setExecuteErr(""); setBackupInfo(null); setConfirmOpen(true); }}
                    disabled={!preview || preview.would_migrate === 0}
                    title={
                        !preview
                            ? "Run preview first"
                            : (preview.would_migrate === 0
                                ? "No eligible enquiries to migrate"
                                : "Open confirmation")
                    }
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-terracotta text-offwhite text-[12px] font-manrope disabled:opacity-40">
                    <DatabaseZap size={12} /> Execute migration…
                </button>
                <Link
                    data-testid="migration-audit-link"
                    to="/admin/tools?tab=audit"
                    className="text-[12px] text-charcoal underline decoration-dotted">
                    view audit trail
                </Link>
            </div>

            {previewErr && (
                <div data-testid="migration-preview-error"
                     className="text-[12px] text-terracotta mt-2">
                    {previewErr}
                </div>
            )}

            {preview && (
                <div data-testid="migration-preview-summary"
                     className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 text-[12px]">
                    <SummaryCell label="scanned" value={preview.scanned} />
                    <SummaryCell label="would migrate" value={preview.would_migrate} tone="terracotta" />
                    <SummaryCell label="already migrated" value={preview.already_migrated} />
                    <SummaryCell label="malformed" value={preview.malformed} tone={preview.malformed > 0 ? "warn" : ""} />
                    <SummaryCell label="report id" value={String(preview.report_id).slice(0, 8) + "…"} />
                </div>
            )}

            {executeResult && (
                <div data-testid="migration-execute-summary"
                     className="mt-3 rounded-md border border-charcoal/20 bg-offwhite/50 p-2 text-[12px]">
                    Backup audit {String(backupInfo?.auditId || "").slice(0, 8)}… ·{" "}
                    Migration executed: scanned {executeResult.scanned},
                    migrated {executeResult.would_migrate},
                    already-migrated {executeResult.already_migrated},
                    malformed {executeResult.malformed} —
                    report {String(executeResult.report_id).slice(0, 8)}…
                </div>
            )}

            {confirmOpen && (
                <div
                    data-testid="migration-confirm-modal"
                    className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
                    onClick={() => !executing && setConfirmOpen(false)}>
                    <div
                        className="bg-white rounded-lg border border-[#E5E2DC] max-w-md w-full p-5 space-y-3"
                        onClick={e => e.stopPropagation()}>
                        <div className="font-manrope font-semibold text-[15px] text-charcoal">
                            Confirm production migration
                        </div>
                        <p className="text-[12px] text-[#555]">
                            You are about to run the legacy → WS1
                            migration against environment{" "}
                            <code className="text-terracotta font-semibold">{env}</code>.
                            A full database backup will be downloaded
                            first — the migration only runs if that
                            backup completes successfully. To
                            continue, type the environment name
                            exactly.
                        </p>
                        <label className="block text-[11px] text-[#666]">Environment name</label>
                        <input
                            data-testid="migration-confirm-env-input"
                            type="text"
                            value={confirmText}
                            onChange={e => setConfirmText(e.target.value)}
                            disabled={executing}
                            className="w-full px-2 py-1.5 rounded border border-[#E5E2DC] text-[13px] focus:outline-none focus:border-terracotta disabled:opacity-60" />
                        <label className="flex items-center gap-2 text-[12px] text-[#333]">
                            <input
                                data-testid="migration-confirm-ack-checkbox"
                                type="checkbox"
                                checked={ack}
                                disabled={executing}
                                onChange={e => setAck(e.target.checked)} />
                            I acknowledge this operation will write to
                            the <code>{env}</code> database.
                        </label>

                        <div className="mt-1 rounded-md border border-[#E5E2DC] p-2 bg-offwhite/40">
                            <div className="text-[11px] font-semibold text-charcoal mb-1.5">
                                Backup engine <span className="text-terracotta">*</span>
                            </div>
                            <div className="text-[10.5px] text-[#666] mb-2 leading-snug">
                                Choose which engine takes the pre-migration
                                backup. mongodump needs the binary in the
                                container; Python fallback works anywhere.
                                The migration only proceeds if the archive
                                you download matches the engine you pick here.
                            </div>
                            <label className="flex items-center gap-2 text-[12px] text-[#333]">
                                <input
                                    data-testid="migration-backup-engine-mongodump"
                                    type="radio"
                                    name="backup-engine"
                                    value="mongodump"
                                    checked={backupEngine === "mongodump"}
                                    disabled={executing}
                                    onChange={() => setBackupEngine("mongodump")} />
                                <span><code>mongodump</code> (native)</span>
                            </label>
                            <label className="flex items-center gap-2 text-[12px] text-[#333] mt-1">
                                <input
                                    data-testid="migration-backup-engine-python"
                                    type="radio"
                                    name="backup-engine"
                                    value="python"
                                    checked={backupEngine === "python"}
                                    disabled={executing}
                                    onChange={() => setBackupEngine("python")} />
                                <span>Python fallback (pure-Python)</span>
                            </label>
                        </div>
                        {phase === "backup" && (
                            <div data-testid="migration-phase-backup"
                                 className="text-[12px] text-[#333] flex items-center gap-2">
                                <Loader2 size={12} className="animate-spin" />
                                Step 1 of 2: downloading pre-migration backup…
                            </div>
                        )}
                        {phase === "migrate" && (
                            <div data-testid="migration-phase-migrate"
                                 className="text-[12px] text-[#333] flex items-center gap-2">
                                <Loader2 size={12} className="animate-spin" />
                                Step 2 of 2: running migration (backup audit{" "}
                                {String(backupInfo?.auditId || "").slice(0, 8)}…)
                            </div>
                        )}
                        {executeErr && (
                            <div data-testid="migration-execute-error"
                                 className="text-[12px] text-terracotta">
                                {executeErr}
                            </div>
                        )}
                        <div className="flex justify-end gap-2">
                            <button
                                data-testid="migration-confirm-cancel"
                                onClick={() => setConfirmOpen(false)}
                                disabled={executing}
                                className="px-3 py-1.5 rounded-md text-[12px] font-manrope text-charcoal border border-[#E5E2DC]">
                                Cancel
                            </button>
                            <button
                                data-testid="migration-confirm-run"
                                onClick={runExecute}
                                disabled={!canExecute || executing}
                                className="px-3 py-1.5 rounded-md text-[12px] font-manrope bg-terracotta text-offwhite disabled:opacity-40">
                                {executing ? <Loader2 size={12} className="animate-spin inline" /> : null} Backup &amp; migrate
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function SummaryCell({ label, value, tone }) {
    const toneCls =
        tone === "terracotta" ? "text-terracotta"
        : tone === "warn" ? "text-[#c47a00]"
        : "text-charcoal";
    return (
        <div className="rounded border border-[#E5E2DC] p-2 bg-white">
            <div className="text-[10px] uppercase tracking-wide text-[#888]">{label}</div>
            <div className={`font-manrope text-[16px] font-semibold ${toneCls}`}>{value ?? "—"}</div>
        </div>
    );
}



// ---------------------------------------------------------------------------
// §12 — WhatsApp Message Builder utility. Manual only. Nothing sent, nothing
// persisted. Client-side template rendering. Structure keyed by
// message_type for future additions.
// ---------------------------------------------------------------------------
const MESSAGE_TYPES = {
    initial_contact_after_email: {
        label: "Initial Contact after email",
        title: "Initial Contact after email",
        requiredMergeFields: ["first_name", "project_postcode"],
        template:
`Hi {{first_name}},

It’s Phillip from London Renovation Specialists. I’m just following up on your enquiry about your project at {{project_postcode}}.

I sent you an email, but I wanted to follow up here as well in case you missed it.

We’ve reviewed the information you submitted and would be happy to discuss your project in more detail.

Before we speak, it would be helpful if you could send me the full address of the property, along with any other information you have that might be relevant to the project (for example, existing architect or estate agent plans).

Once we have that, we can review everything ahead of a short introductory call to discuss your project, what you’re hoping to achieve and the next steps. Please also let me know when you would be available for a call.

Thank you, and I look forward to hearing from you.

Phillip`,
    },
};


// Exported for unit-testing. Normalise a UK phone to intl `447xxxxxxxxx`.
// Returns "" if input cannot be safely normalised.
export function normaliseUkPhoneForWhatsapp(raw) {
    if (!raw) return "";
    // Strip spaces, dashes, parens, non-breaking spaces.
    const stripped = String(raw).replace(/[\s\-().\u00A0]/g, "");
    // Only digits remain except a possible leading `+`.
    if (/^\+447\d{9}$/.test(stripped)) return stripped.slice(1);
    if (/^00447\d{9}$/.test(stripped)) return stripped.slice(2);
    if (/^447\d{9}$/.test(stripped)) return stripped;
    if (/^07\d{9}$/.test(stripped)) return "44" + stripped.slice(1);
    return "";
}


// Exported for unit-testing. Derive first_name per platform convention.
export function deriveFirstName(fullName) {
    return String(fullName || "").trim().split(" ", 1)[0] || "";
}


// Exported for unit-testing. Render the template with merges.
export function renderWhatsappMessage(messageTypeKey, mergeValues) {
    const spec = MESSAGE_TYPES[messageTypeKey];
    if (!spec) return "";
    let out = spec.template;
    for (const [k, v] of Object.entries(mergeValues || {})) {
        out = out.split(`{{${k}}}`).join(String(v ?? ""));
    }
    return out;
}


function WhatsAppBuilderSection() {
    const [open, setOpen] = useState(false);
    return (
        <section className="rounded-lg border border-[#E5E2DC] bg-white p-5"
            data-testid="whatsapp-builder-section">
            <div className="flex items-center gap-2 mb-2">
                <MessageCircle size={16} className="text-charcoal" />
                <h2 className="font-manrope font-semibold text-[15px] text-charcoal">WhatsApp Message Builder</h2>
            </div>
            <p className="text-[13px] text-[#666] mb-3">
                Generate pre-populated WhatsApp messages using current
                project/client data. Nothing is sent externally.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="whatsapp-builder-launchers">
                {Object.entries(MESSAGE_TYPES).map(([key, spec]) => (
                    <button key={key} type="button"
                        onClick={() => setOpen(key)}
                        data-testid={`whatsapp-builder-launch-${key}`}
                        className="text-[13px] px-3 py-1.5 rounded border border-charcoal text-charcoal hover:bg-[#FAFAFA]">
                        {spec.label}
                    </button>
                ))}
            </div>
            {open && (
                <WhatsAppBuilderModal
                    messageTypeKey={open}
                    onClose={() => setOpen(false)} />
            )}
        </section>
    );
}


function WhatsAppBuilderModal({ messageTypeKey, onClose }) {
    const spec = MESSAGE_TYPES[messageTypeKey];
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);   // { project_id, client_idx, full_name, postcode, phone, email }
    const [messageText, setMessageText] = useState("");
    const [copied, setCopied] = useState(false);
    const [err, setErr] = useState("");

    // Debounced search.
    useEffect(() => {
        const q = (query || "").trim();
        const t = setTimeout(() => {
            api.get(`/admin/tools/whatsapp-builder/search?q=${encodeURIComponent(q)}&limit=20`)
                .then(r => setResults(r.data?.items || []))
                .catch(() => setResults([]));
        }, 300);
        return () => clearTimeout(t);
    }, [query]);

    // Regenerate on project change (discards prior edits by design).
    useEffect(() => {
        if (!selected) { setMessageText(""); setErr(""); return; }
        const fn = deriveFirstName(selected.full_name);
        const pc = String(selected.postcode || "").trim();
        const missing = [];
        if (!fn) missing.push("client name");
        if (!pc) missing.push("postcode");
        if (missing.length) {
            setMessageText("");
            setErr(`This project is missing ${missing.join(" and ")} — cannot generate message. Edit the project details first.`);
            return;
        }
        setErr("");
        setMessageText(renderWhatsappMessage(messageTypeKey, {
            first_name: fn, project_postcode: pc,
        }));
    }, [selected, messageTypeKey]);

    const phoneNormalised = selected ? normaliseUkPhoneForWhatsapp(selected.phone) : "";
    const canOpenWa = !!(selected && phoneNormalised && messageText);
    const canCopy = !!(selected && messageText);

    const doCopy = async () => {
        try {
            await navigator.clipboard.writeText(messageText);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    };
    const doOpenWa = () => {
        if (!canOpenWa) return;
        const url = `https://wa.me/${phoneNormalised}?text=${encodeURIComponent(messageText)}`;
        window.open(url, "_blank", "noopener");
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
            data-testid="whatsapp-builder-modal">
            <div className="bg-white rounded shadow-lg w-[min(720px,94vw)] max-h-[92vh] overflow-auto p-5">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-manrope font-semibold text-[15px] text-charcoal"
                        data-testid="whatsapp-builder-modal-title">{spec.title}</h3>
                    <button type="button" onClick={onClose}
                        data-testid="whatsapp-builder-close"
                        className="text-[13px] text-[#666] hover:text-charcoal">Close</button>
                </div>

                <label className="text-[12px] text-[#666] block mb-1">Project</label>
                <input value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder="Search by name, postcode, or email"
                    data-testid="whatsapp-builder-search"
                    className="w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px] mb-2" />
                <div className="max-h-[160px] overflow-auto border border-[#E5E2DC] rounded mb-3"
                    data-testid="whatsapp-builder-results">
                    {results.length === 0 && (
                        <div className="text-[12px] text-[#888] p-2">No matches.</div>
                    )}
                    {results.map(r => {
                        const rowKey = `${r.project_id}:${r.client_idx}`;
                        const isSelected = selected
                            && selected.project_id === r.project_id
                            && selected.client_idx === r.client_idx;
                        return (
                            <button key={rowKey} type="button"
                                onClick={() => setSelected(r)}
                                data-testid={`whatsapp-builder-result-${r.project_id}-${r.client_idx}`}
                                className={`w-full text-left text-[13px] px-2 py-1.5 hover:bg-[#FAFAFA] ${
                                    isSelected ? "bg-[#F5F3EE]" : ""}`}>
                                <span className="font-manrope">{r.full_name || "(no name)"}</span>
                                <span className="ml-2 inline-block text-[11px] text-charcoal/70 bg-charcoal/5 border border-charcoal/10 rounded px-1 py-0.5"
                                    data-testid={`whatsapp-builder-result-${r.project_id}-${r.client_idx}-chip`}>
                                    C{(r.client_idx || 0) + 1}
                                </span>
                                <span className="text-[#666]"> · {r.postcode || "(no postcode)"}</span>
                            </button>
                        );
                    })}
                </div>

                {selected && (
                    <>
                        {err ? (
                            <div className="text-[12px] text-terracotta mb-3"
                                data-testid="whatsapp-builder-validation">{err}</div>
                        ) : (
                            <textarea rows={14} value={messageText}
                                onChange={e => setMessageText(e.target.value)}
                                data-testid="whatsapp-builder-textarea"
                                className="w-full border border-[#E5E2DC] rounded px-2 py-2 text-[13px] font-inter mb-2" />
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                            <button type="button" onClick={doCopy} disabled={!canCopy}
                                data-testid="whatsapp-builder-copy"
                                className="text-[13px] px-3 py-1.5 rounded border border-charcoal text-charcoal disabled:opacity-50">
                                {copied ? "Copied" : "Copy message"}
                            </button>
                            <button type="button" onClick={doOpenWa} disabled={!canOpenWa}
                                data-testid="whatsapp-builder-open"
                                className="text-[13px] px-3 py-1.5 rounded bg-[#25D366] text-white disabled:opacity-50">
                                Open in WhatsApp
                            </button>
                            {selected && !phoneNormalised && (
                                <span className="text-[11.5px] text-[#666]"
                                    data-testid="whatsapp-builder-phone-hint">
                                    No usable client phone number is available for this project.
                                </span>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
